import { api } from './client'

export interface DeleteRequest {
  qboCompanyId: string | null
  entityType: string
  source: 'filter' | 'refs'
  dateRange?: string
  from?: string
  to?: string
  amountMin?: string
  amountMax?: string
  references?: string[]
  /**
   * The exact QBO record ids the user kept on the review step. When present only
   * these are deleted; the filters become decorative because the user already
   * narrowed the set interactively.
   */
  targetIds?: string[]
}

export interface DeleteSearchItem {
  qboId: string
  syncToken: string | null
  docNumber: string | null
  txnDate: string | null
  name: string | null
  amount: string | null
  status: string | null
  /** The full field bag from QuickBooks, so the review table can show
   * the same columns as Export. */
  fields?: Record<string, unknown> | null
  /**
   * This record's cells, already resolved against the export template — one
   * inner array per business line, each the same length as `exportColumns`.
   *
   * Rows hang off the record rather than arriving as one flat list because the
   * two are not one-to-one: a journal entry yields one row per line, but
   * deleting any of them deletes the same record. Rendering the lines while
   * keeping selection at record level is the point.
   */
  rows?: string[][] | null
}

export interface DeleteSearchResult {
  items: DeleteSearchItem[]
  total: number
  limit: number
  /**
   * The template's column labels in template order — matching Export's columns.
   * Null when no template exists for this entity.
   */
  exportColumns?: string[] | null
}

export interface DeleteSearchRequest {
  qboCompanyId: string
  entityType: string
  source: 'filter' | 'refs'
  /** One document reference per line, refs mode only. */
  reference?: string
  dateRange?: string
  from?: string
  to?: string
  amountMin?: string
  amountMax?: string
  limit?: number
}

/**
 * `POST /api/delete/search` runs the same resolution the delete job uses and
 * returns the real records it will target, so the review step shows genuine
 * QuickBooks rows instead of placeholders.
 *
 * `POST /api/delete/submit` re-resolves server-side; nothing in the preview is
 * trusted to delete, and the actual delete is queued as a real job.
 */
export const deleteApi = {
  search(request: DeleteSearchRequest): Promise<DeleteSearchResult> {
    return api.post<DeleteSearchResult>('/delete/search', request)
  },

  submit(request: DeleteRequest): Promise<{ jobId: string }> {
    return api.post<{ jobId: string }>('/delete/submit', request)
  },
}
